import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signOut as firebaseSignOut,
  updateProfile,
  type User
} from 'firebase/auth';
import { doc, onSnapshot, runTransaction, serverTimestamp, updateDoc, type Timestamp } from 'firebase/firestore';

import { auth, db, googleProvider, isFirebaseConfigured } from 'firebase/config';
import type { UserRole, UserStatus } from 'firebase/users-service';

export type { UserRole, UserStatus };

// Firestore users/{uid} 문서를 화면에서 다루는 모양 (09 §6.10 RBAC).
// dual-write 전환 기간: level 필드 유지. derive 는 users-service 에서 수행.
export interface UserProfile {
  uid: string;
  email: string | null;
  displayName: string | null;
  status: UserStatus;
  roles: UserRole[];
  level?: number; // geek1 호환 (참고용)
  createdAt?: Timestamp | null;
}

interface AuthContextValue {
  user: User | null;
  userProfile: UserProfile | null;
  loading: boolean;
  isFirebaseConfigured: boolean;
  signInEmail: (email: string, password: string) => Promise<User>;
  signUpEmail: (email: string, password: string, displayName?: string) => Promise<User>;
  signInGoogle: () => Promise<User>;
  signOut: () => Promise<void>;
  hasRole: (role: UserRole) => boolean;
  isAdmin: boolean;
  // 가입 대기/차단 사용자가 로그인 시도하면 자동 signOut 후 메시지 저장 — Login 페이지가 Dialog 로 표시
  authError: AuthError | null;
  clearAuthError: () => void;
}

export interface AuthError {
  status: 'pending' | 'rejected';
  title: string;
  message: string;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

async function ensureUserProfile(user: User): Promise<void> {
  // transaction 으로 보호 — getDoc 의 race condition / cache stale 로 인한 사용자 doc 덮어쓰기 사고 (2026-05-24) 후 강화.
  // tx.get 은 server fetch 보장 + tx 안의 set 은 race 시 retry. 진짜 신규 사용자일 때만 setDoc.
  const userRef = doc(db, 'users', user.uid);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(userRef);
    if (snap.exists()) {
      // 기존 사용자 — 절대 status/roles/level 안 건드림.
      return;
    }
    // 진짜 신규 — 기본 프로필 생성. status='pending', roles=[], level=0 (09 §6.10).
    tx.set(userRef, {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName,
      photoURL: user.photoURL,
      status: 'pending',
      roles: [],
      level: 0,
      createdAt: serverTimestamp()
    });
  });
}

// level → status/roles derive — DB 에 새 필드가 없을 때만 fallback
function deriveFromLevel(level: number | undefined): { status: UserStatus; roles: UserRole[] } {
  const l = typeof level === 'number' ? level : 0;
  if (l === 9) return { status: 'active', roles: ['admin'] };
  if (l === 2) return { status: 'active', roles: ['member'] };
  if (l === 1) return { status: 'rejected', roles: [] };
  return { status: 'pending', roles: [] };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState<AuthError | null>(null);

  useEffect(() => {
    if (!isFirebaseConfigured) {
      setLoading(false);
      return;
    }
    const unsubscribeAuth = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
      setLoading(false);
    });
    return unsubscribeAuth;
  }, []);

  useEffect(() => {
    if (!user || !isFirebaseConfigured) {
      setUserProfile(null);
      return;
    }
    void ensureUserProfile(user).catch((err) => console.warn('[auth] ensureUserProfile failed', err));

    // 매 로그인 시 lastSignInAt + photoURL 동기화 (Google 사진 변경 가능).
    // firestore.rules 가 본인 자기 필드만 update 허용. 실패해도 무시 (예: 일시 권한 문제).
    void updateDoc(doc(db, 'users', user.uid), {
      lastSignInAt: serverTimestamp(),
      photoURL: user.photoURL
    }).catch((err) => console.warn('[auth] lastSignInAt/photoURL update failed', err));

    const unsubscribeProfile = onSnapshot(doc(db, 'users', user.uid), (snap) => {
      const data = snap.data();
      if (!data) {
        setUserProfile(null);
        return;
      }
      const level = typeof data.level === 'number' ? data.level : undefined;
      const hasNewFields = data.status !== undefined && Array.isArray(data.roles);
      const { status, roles } = hasNewFields
        ? { status: data.status as UserStatus, roles: data.roles as UserRole[] }
        : deriveFromLevel(level);

      // pending / rejected — 즉시 로그아웃 + authError 저장
      if (status === 'pending') {
        setAuthError({
          status: 'pending',
          title: '가입 승인 대기 중',
          message:
            '관리자의 승인을 받은 후 다시 로그인해주세요.\n승인이 늦어지면 관리자에게 문의해주세요.'
        });
        void firebaseSignOut(auth);
        setUserProfile(null);
        return;
      }
      if (status === 'rejected') {
        setAuthError({
          status: 'rejected',
          title: '접근 차단됨',
          message: '계정이 비활성화되었습니다.\n문의는 관리자에게 부탁드립니다.'
        });
        void firebaseSignOut(auth);
        setUserProfile(null);
        return;
      }

      setUserProfile({
        uid: user.uid,
        email: data.email ?? user.email,
        displayName: data.displayName ?? user.displayName,
        status,
        roles,
        level,
        createdAt: data.createdAt ?? null
      });
    });
    return unsubscribeProfile;
  }, [user]);

  const signInEmail = async (email: string, password: string) => {
    const credential = await signInWithEmailAndPassword(auth, email, password);
    return credential.user;
  };

  const signUpEmail = async (email: string, password: string, displayName?: string) => {
    const credential = await createUserWithEmailAndPassword(auth, email, password);
    if (displayName) {
      await updateProfile(credential.user, { displayName });
    }
    return credential.user;
  };

  const signInGoogle = async () => {
    const credential = await signInWithPopup(auth, googleProvider);
    return credential.user;
  };

  const signOut = async () => {
    await firebaseSignOut(auth);
  };

  const hasRole = (role: UserRole) =>
    !!userProfile && userProfile.status === 'active' && userProfile.roles.includes(role);

  const clearAuthError = () => setAuthError(null);

  const value: AuthContextValue = {
    user,
    userProfile,
    loading,
    isFirebaseConfigured,
    signInEmail,
    signUpEmail,
    signInGoogle,
    signOut,
    hasRole,
    isAdmin: hasRole('admin'),
    authError,
    clearAuthError
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
